import {
  cloneDeep,
  find,
  forEach,
  get,
  hasIn,
  indexOf,
  isEmpty,
  isNaN,
  isNil,
  keys,
  map,
  set
} from 'lodash';
import turf from '@turf/turf';
import * as topojson from 'topojson-client';

import * as layerOps from '../api/Layers/layer-operations';
import { LAYER_SUBTYPES } from '../api/Layers/Layers';

import adaptURL from './adapt-url';
import getGeoJSONFromCSV from './get-geojson-from-csv';
import getGeoJSONFromExcel from './get-geojson-from-excel';
import {
  getThematicRowBySorensenDice,
  getGeoJSONFeatureBySorensenDice
} from './get-sorensen-dice-score';
import loadCSVTable from './load-csv-table';
import loadExcelFromURL from './load-excel-from-url';
import loadGeoJSONFromURL from './load-geojson-from-url';
import methodPromise from './method-promise-wrapper';

const SCORE_PROP = 'Similarity Index';

/**
 * Joins the thematic and the geojson by an inner join.
 *
 * @param {object} arg - The arguments for the function.
 * @param {object} arg.geojson - The fetched geojson to join.
 * @param {string} arg.geojsonPropToJoin - The name of the property of the GeoJSON.
 * @param {string} arg.thematicTable - The fetched table to join.
 * @param {string} arg.indexOfHeader - The index of the thematic key on the table.
 * @param {number} arg.sorensenDiceMinimun - The minimun value for the Sorensen-Dice to join.
 * @returns {object} The joined layer (GeoJSON).
 * @throws {Error} If something goes wrong.
 */
const innerJoin = ({
  geojson,
  geojsonPropToJoin,
  thematicTable,
  indexOfHeader,
  sorensenDiceMinimun
}) => {
  const joinedGeoJSON = cloneDeep(geojson);
  turf.propEach(joinedGeoJSON, (currentProperties, featureIndex) => {
    if (!isNil(currentProperties[geojsonPropToJoin])) {
      let thematicRow = [];
      let score = '';
      if (!isNaN(Number(currentProperties[geojsonPropToJoin]))) {
        const geojsonValue = currentProperties[geojsonPropToJoin];
        thematicRow = find(
          thematicTable,
          auxRow => Number(auxRow[indexOfHeader]) === Number(geojsonValue)
        );
      } else {
        const geojsonValue = `${currentProperties[geojsonPropToJoin]}`;
        thematicRow = find(thematicTable, auxRow => auxRow[indexOfHeader] === geojsonValue);
        if (isNil(thematicRow)) {
          ({ thematicRow, score } = getThematicRowBySorensenDice({
            thematicTable,
            indexOfHeader,
            geojsonValue,
            sorensenDiceMinimun
          }));
          if (!isNil(thematicRow))
            set(
              joinedGeoJSON,
              ['features', featureIndex, 'properties', SCORE_PROP],
              `${(score * 100).toFixed(2)}%`
            );
        }
      }
      if (!isNil(thematicRow))
        forEach(thematicRow, (value, index) => {
          const isToAdd =
            !hasIn(geojson, ['features', featureIndex, 'properties', thematicTable[0][index]]) &&
            index !== indexOfHeader;
          if (isToAdd)
            set(
              joinedGeoJSON,
              ['features', featureIndex, 'properties', thematicTable[0][index]],
              isNaN(Number(value)) ? value : Number(value)
            );
        });
      else
        throw new Error(
          `The GeoJSON property ${geojsonPropToJoin}: ${currentProperties[geojsonPropToJoin]} found no match on the csv`
        );
    }
  });
  return joinedGeoJSON;
};

/**
 * Joins the thematic and the geojson by a right join.
 *
 * @param {object} arg - The arguments for the function.
 * @param {object} arg.geojson - The fetched geojson to join.
 * @param {string} arg.geojsonPropToJoin - The name of the property of the GeoJSON.
 * @param {string} arg.thematicTable - The fetched table to join.
 * @param {string} arg.indexOfHeader - The index of the thematic key on the table.
 * @param {number} arg.sorensenDiceMinimun - The minimun value for the Sorensen-Dice to join.
 * @returns {object} The joined layer (GeoJSON).
 */
const rightJoin = ({
  geojson,
  geojsonPropToJoin,
  thematicTable,
  indexOfHeader,
  sorensenDiceMinimun
}) => {
  const joinedGeoJSON = cloneDeep(geojson);
  joinedGeoJSON.features = [];
  const thematicCol = map(thematicTable, row => row[indexOfHeader]).slice(1);
  forEach(thematicCol, (thematicValue, thematicRowIndex) => {
    if (!isNil(thematicValue)) {
      let geojsonFeature = {};
      let score = '';
      if (!isNaN(Number(thematicValue))) {
        geojsonFeature = find(
          geojson.features,
          feature =>
            Number(get(feature, ['properties', geojsonPropToJoin])) === Number(thematicValue)
        );
      } else {
        geojsonFeature = find(
          geojson.features,
          feature => get(feature, ['properties', geojsonPropToJoin]) === thematicValue
        );
        if (isNil(geojsonFeature)) {
          ({ geojsonFeature, score } = getGeoJSONFeatureBySorensenDice({
            geojson,
            geojsonPropToJoin,
            thematicValue,
            sorensenDiceMinimun
          }));
          if (!isNil(geojsonFeature))
            geojsonFeature.properties[SCORE_PROP] = `${(score * 100).toFixed(2)}%`;
        }
      }
      if (!isNil(geojsonFeature)) {
        const thematicRow = thematicTable[thematicRowIndex + 1];
        forEach(thematicRow, (value, index) => {
          const isToAdd =
            !hasIn(geojsonFeature, ['properties', thematicTable[0][index]]) &&
            index !== indexOfHeader;
          if (isToAdd)
            set(
              geojsonFeature,
              ['properties', thematicTable[0][index]],
              isNaN(Number(value)) ? value : Number(value)
            );
        });
        joinedGeoJSON.features.push(geojsonFeature);
      }
    }
  });
  return joinedGeoJSON;
};

/**
 * Joins the thematic and the geojson by a left join.
 *
 * @param {object} arg - The arguments for the function.
 * @param {object} arg.geojson - The fetched geojson to join.
 * @param {string} arg.geojsonPropToJoin - The name of the property of the GeoJSON.
 * @param {string} arg.thematicTable - The fetched table to join.
 * @param {string} arg.indexOfHeader - The index of the thematic key on the table.
 * @param {number} arg.sorensenDiceMinimun - The minimun value for the Sorensen-Dice to join.
 * @returns {object} The joined layer (GeoJSON).
 */
const leftJoin = ({
  geojson,
  geojsonPropToJoin,
  thematicTable,
  indexOfHeader,
  sorensenDiceMinimun
}) => {
  const joinedGeoJSON = rightJoin({
    geojson,
    geojsonPropToJoin,
    thematicTable,
    indexOfHeader,
    sorensenDiceMinimun
  });
  turf.propEach(geojson, (currentProperties, featureIndex) => {
    const property = currentProperties[geojsonPropToJoin];
    const joinedProperty =
      find(
        joinedGeoJSON.features,
        joinedFeature => get(joinedFeature, ['properties', geojsonPropToJoin]) === property
      ) || '';
    if (isEmpty(joinedProperty)) joinedGeoJSON.features.push(geojson.features[featureIndex]);
  });
  return joinedGeoJSON;
};

/**
 * Joins a GeoJSON and a Thematic files.
 *
 * @async
 * @param {object} arg - The arguments for the function.
 * @param {object} arg.geojsonId - The id of the GeoJSON layer.
 * @param {string} arg.thematicId - The id of the thematic layer.
 * @param {string} arg.geojsonPropToJoin - The name of the property of the GeoJSON.
 * @param {string} arg.thematicKeyToJoin - The name of the key element of the Thematic.
 * @param {number} arg.sorensenDiceMinimun - The minimun value for the Sorensen-Dice to join.
 * @param {string} arg.layerSubType - The subtype of the join layer to use.
 * @returns {object} The joined layer (GeoJSON).
 * @throws {Error} If something goes wrong.
 */
const joinGeoJSONThematicLayer = async ({
  geojsonId,
  thematicId,
  geojsonPropToJoin,
  thematicKeyToJoin,
  layerSubType,
  sorensenDiceMinimun
}) => {
  try {
    const geojsonLayer = await methodPromise('layers.fetchOne', geojsonId);
    const thematicLayer = await methodPromise('layers.fetchOne', thematicId);

    let geojson = {};
    let topojsonFile = {};
    let objectKey = {};
    const geojsonUrl = adaptURL(layerOps.getURL(geojsonLayer));
    switch (true) {
      case layerOps.isCSV(geojsonLayer):
        geojson = await getGeoJSONFromCSV(geojsonLayer);
        break;
      case layerOps.isExcel(geojsonLayer):
      case layerOps.isGoogleSheets(geojsonLayer):
        geojson = await getGeoJSONFromExcel(geojsonLayer);
        break;
      case layerOps.isFiware(geojsonLayer):
        geojson = await methodPromise('layers.loadFiware', {
          fiwareLayerName: layerOps.getFiwareLayerName(geojsonLayer),
          georefType: layerOps.getGeorefType(geojsonLayer),
          loadParameters: {
            ...layerOps.getFiwareParams(geojsonLayer),
            fiwareTokenURL: layerOps.getURL(geojsonLayer)
          },
          spatialTableFields: layerOps.getTableSpatialObject(geojsonLayer),
          locationIndex: layerOps.getLocationIndex(geojsonLayer),
          addressIndex: layerOps.getAddressIndex(geojsonLayer)
        });
        break;
      case layerOps.isGeoJSON(geojsonLayer):
      case layerOps.isGeoJSONVT(geojsonLayer):
      case layerOps.isRealTime(geojsonLayer):
        geojson = await loadGeoJSONFromURL(geojsonUrl);
        break;
      case layerOps.isOGR(geojsonLayer):
        geojson = await methodPromise('layers.loadOGRGeoJSON', {
          url: geojsonUrl,
          layerName: layerOps.getOGRLayerName(geojsonLayer),
          attribute: layerOps.getOGRAttributes(geojsonLayer).split(',')
        });
        break;
      case layerOps.isPostgreSQL(geojsonLayer):
        geojson = await methodPromise('layers.loadPostgreSQL', {
          loadParameters: layerOps.getConnectionParams(geojsonLayer),
          attributes: map(layerOps.getGeoJSONProps(geojsonLayer), 'name')
        });
        break;
      case layerOps.isTopoJSON(geojsonLayer):
        topojsonFile = await loadGeoJSONFromURL(geojsonUrl);
        [objectKey] = keys(topojsonFile.objects);
        geojson = topojson.feature(topojsonFile, topojsonFile.objects[objectKey]);
        break;
      default:
        throw new Error('Invalid spatial layer subtype');
    }

    let thematicTable = [];
    let tableTitle = [];
    let tableBody = [];
    switch (true) {
      case layerOps.isCSV(thematicLayer):
        ({ tableBody, tableTitle } = await loadCSVTable(thematicLayer));
        thematicTable = [tableTitle, ...tableBody];
        break;
      case layerOps.isExcel(thematicLayer):
      case layerOps.isGoogleSheets(thematicLayer):
        ({ tableBody, tableTitle } = await loadExcelFromURL(thematicLayer));
        thematicTable = [tableTitle, ...tableBody];
        break;
      case layerOps.isFiware(thematicLayer):
        thematicTable = await methodPromise('layers.loadFiware', {
          fiwareLayerName: layerOps.getFiwareLayerName(thematicLayer),
          loadParameters: {
            ...layerOps.getFiwareParams(thematicLayer),
            fiwareTokenURL: layerOps.getURL(thematicLayer)
          },
          isThematic: true
        });
        break;
      case layerOps.isPostgreSQL(thematicLayer):
        thematicTable = await methodPromise('layers.loadPostgreSQL', {
          loadParameters: layerOps.getConnectionParams(thematicLayer),
          attributes: layerOps.getThematicKeyList(thematicLayer),
          isThematic: true
        });
        break;
      default:
        throw new Error('Invalid thematic layer subtype');
    }

    let joinedGeoJSON = {};
    const indexOfHeader = indexOf(thematicTable[0], thematicKeyToJoin);
    switch (layerSubType) {
      case LAYER_SUBTYPES.INNERJOIN:
        joinedGeoJSON = innerJoin({
          geojson,
          geojsonPropToJoin,
          thematicTable,
          indexOfHeader,
          sorensenDiceMinimun
        });
        break;
      case LAYER_SUBTYPES.RIGHTJOIN:
        joinedGeoJSON = rightJoin({
          geojson,
          geojsonPropToJoin,
          thematicTable,
          indexOfHeader,
          sorensenDiceMinimun
        });
        break;
      case LAYER_SUBTYPES.LEFTJOIN:
        joinedGeoJSON = leftJoin({
          geojson,
          geojsonPropToJoin,
          thematicTable,
          indexOfHeader,
          sorensenDiceMinimun
        });
        break;
      default:
        throw new Error('Invalid or missing layer subtype.');
    }

    return joinedGeoJSON;
  } catch (err) {
    throw new Error(err.message);
  }
};

export default joinGeoJSONThematicLayer;
